import React, { useState, useCallback } from "react";
import {
  Stack,
  Group,
  Button,
  Paper,
  Text,
  SimpleGrid,
  Badge,
  LoadingOverlay,
} from "@mantine/core";
import {
  IconExternalLink,
  IconRefresh,
  IconUsers,
  IconCalendar,
  IconUserCheck,
  IconUserX,
  IconCoffee,
} from "@tabler/icons-react";
import { notifyError, notifySuccess, notifyWarning } from "../../utils/notify";
import moment from "moment";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { usePayrollPeriod } from "../../hooks/usePayrollPeriod";
import { usePayrollUserSync } from "../../hooks/usePayrollUserSync";
import { openPayrollDashboard } from "../../utils/payrollUtils";
import { Employee } from "../../types/salon";
import { BusinessInfo } from "../../types/context";
import {
  TimeTrackingState,
  TimeCard,
  TimesheetData,
} from "../../types/timeTracking";
import { GetTransactionsResponse } from "../../types/transactions";
import {
  SyncPayrollRequest,
  RollFiUser,
  PrepPayrollSyncResponse,
} from "../../types/rollfi";
import PayrollSyncConfirmationModal from "./PayrollSyncConfirmationModal";
import IncompleteTransactionsModal from "./IncompleteTransactionsModal";
import { FINAL_APPOINTMENT_STATUSES } from "../../utils/transactionUtils";

interface SalonContextValue {
  currentUser: Employee | null;
  businessInfo: BusinessInfo | null;
  employees: Employee[];
  isLoading: boolean;
}

type EmployeeStatusMap = Map<string, TimeTrackingState>;

interface ManagerOverviewViewProps {
  employeeStatuses: EmployeeStatusMap;
  isLoadingStatuses: boolean;
  eligibleEmployees: Employee[];
  timesheetData: TimesheetData;
  onTimesheetRefresh: () => Promise<TimesheetData>;
}

/**
 * Helper function to check for incomplete transactions in the pay period
 * @param businessId - Business/salon ID
 * @param payrollPeriod - Current payroll period with date range
 * @param getTransactions - Function to fetch transactions
 * @returns Array of incomplete transactions (non-quick sales with non-final appointment status)
 */
async function checkForIncompleteTransactions(
  businessId: string,
  payrollPeriod: { payBeginDate: string; payEndDate: string },
  getTransactions: (
    salonId: string,
    queryParams: string,
  ) => Promise<{ transactions: GetTransactionsResponse[] }>,
): Promise<GetTransactionsResponse[]> {
  const startDate = moment(payrollPeriod.payBeginDate).format("YYYY-MM-DD");
  const endDate = moment(payrollPeriod.payEndDate).format("YYYY-MM-DD");

  const transactionsResponse = await getTransactions(
    businessId,
    `start_date=${startDate}&end_date=${endDate}`,
  );

  const transactions = transactionsResponse.transactions || [];

  // Filter for incomplete transactions (non-quick sales with non-final appointment status)
  const incompleteTransactions = transactions.filter(
    (txn: GetTransactionsResponse) => {
      const lineItems = txn.line_items;
      const isQuickSale =
        !lineItems.booked_services || lineItems.booked_services.length === 0;

      // Only check appointment status for non-quick sales
      if (!isQuickSale) {
        const appointmentStatus = txn.transaction.appointment_status;
        return (
          !appointmentStatus ||
          !FINAL_APPOINTMENT_STATUSES.includes(appointmentStatus)
        );
      }

      return false;
    },
  );

  return incompleteTransactions;
}

export default function ManagerOverviewView({
  employeeStatuses,
  isLoadingStatuses,
  eligibleEmployees,
  timesheetData,
  onTimesheetRefresh,
}: ManagerOverviewViewProps) {
  const { isMobile } = useResponsive();
  const salonContext = useSalon() as unknown as SalonContextValue;
  const { businessInfo, employees, isLoading: salonLoading } = salonContext;

  const typedBusinessInfo = businessInfo as BusinessInfo | null;
  const typedEmployees = employees as Employee[];

  const {
    getTransactions,
    syncRollfiPayroll,
    getRollfiActiveUsers,
    prepPayrollSync,
    getEmployeePayPeriodTimeCard,
  } = useApp();
  const { syncPayrollUsers, isSyncing: isSyncingUsers } = usePayrollUserSync();
  const payrollPeriod = usePayrollPeriod(typedBusinessInfo);

  // Payroll sync modal state
  const [syncModalOpened, setSyncModalOpened] = useState(false);
  const [prepSyncResponse, setPrepSyncResponse] =
    useState<PrepPayrollSyncResponse | null>(null);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);

  // Incomplete transactions modal state
  const [
    incompleteTransactionsModalOpened,
    setIncompleteTransactionsModalOpened,
  ] = useState(false);
  const [incompleteTransactions, setIncompleteTransactions] = useState<
    GetTransactionsResponse[]
  >([]);

  // Calculate clocked in/out/on break counts from shared status
  const statusValues = Array.from(employeeStatuses.values());
  const onBreakCount = statusValues.filter((status) => status.isOnBreak).length;
  const clockedInCount = statusValues.filter(
    (status) => status.isClockedIn && !status.isOnBreak,
  ).length;
  const clockedOutCount =
    eligibleEmployees.length - clockedInCount - onBreakCount;

  // Fetch payroll data via backend prep-payroll-sync endpoint
  const fetchPayrollData = useCallback(async () => {
    if (
      !typedBusinessInfo?.id ||
      !typedBusinessInfo?.rollfi__company_id ||
      !payrollPeriod
    )
      return;

    try {
      setIsLoadingTransactions(true);
      const companyId = typedBusinessInfo.rollfi__company_id;
      const startDate = moment(payrollPeriod.payBeginDate).format("YYYY-MM-DD");
      const endDate = moment(payrollPeriod.payEndDate).format("YYYY-MM-DD");

      // Fetch RollFi active users and refresh timesheet data in parallel
      const [activeUsersResponse, freshTimesheetData] = await Promise.all([
        getRollfiActiveUsers({ companyId, workerType: "W2" }),
        onTimesheetRefresh(),
      ]);

      const users: RollFiUser[] = activeUsersResponse.user || [];
      const timeCards = freshTimesheetData.timeCards || [];

      // Build staff array: for each employee with a rollfi__user_id,
      // find matching RollFi user for hourlyRate, and timecard for approvedHours.
      // For syncable time cards, fetch per-employee detail so we can populate
      // approvedHoursBreakdown — the backend uses per-day hours for
      // NUM_SERVICES commission calculations.
      const eligibleEmps = (typedEmployees || []).filter(
        (emp) => emp.rollfi__user_id,
      );

      const staff = await Promise.all(
        eligibleEmps.map(async (emp) => {
          const rollfiUser = users.find(
            (u) => u.userId === emp.rollfi__user_id,
          );
          const wage = rollfiUser?.userWages?.[0];
          const hourlyRate =
            wage?.WageBasis?.WageBasis === "Per Hour" && wage.WageRate > 0
              ? wage.WageRate
              : null;

          const tc = timeCards.find(
            (c: TimeCard) => c.userId === emp.rollfi__user_id,
          );
          // Include both Approved and Synced time cards — Synced entries
          // have already been sent to payroll but remain eligible for re-sync.
          const isSyncable =
            tc?.approvalStatus === "Approved" ||
            tc?.approvalStatus === "Synced";
          const approvedHours = isSyncable
            ? (tc.regularHours ?? 0) + (tc.overtimeHours ?? 0)
            : 0;

          // Only drill down when the card is syncable — otherwise the
          // breakdown is unused and we can save a network call.
          let approvedHoursBreakdown: { date: string; hours: number }[] = [];
          if (isSyncable && emp.rollfi__user_id) {
            try {
              const detail = await getEmployeePayPeriodTimeCard(
                emp.rollfi__user_id,
                companyId,
                payrollPeriod.payPeriodId,
              );
              const workDates = detail?.payPeriodTimeCard?.workDates || [];
              approvedHoursBreakdown = workDates.map((wd) => ({
                date: wd.workDate,
                hours: (wd.regularHours ?? 0) + (wd.overtimeHours ?? 0),
              }));
            } catch (err) {
              console.error(
                `Failed to fetch time card detail for ${emp.id}:`,
                err,
              );
            }
          }

          return {
            employeeId: emp.id,
            rollfiUserId: emp.rollfi__user_id!,
            approvedHours,
            hourlyRate,
            approvedHoursBreakdown,
          };
        }),
      );

      // Call the prep-payroll-sync endpoint
      const response = await prepPayrollSync({
        companyId,
        payPeriodId: payrollPeriod.payPeriodId,
        startDate,
        endDate,
        staff,
      });

      setPrepSyncResponse(response);
    } catch (error) {
      console.error("Error fetching payroll data:", error);
      setPrepSyncResponse(null);
    } finally {
      setIsLoadingTransactions(false);
    }
  }, [
    typedBusinessInfo,
    payrollPeriod,
    getRollfiActiveUsers,
    onTimesheetRefresh,
    typedEmployees,
    prepPayrollSync,
    getEmployeePayPeriodTimeCard,
  ]);

  // Handle sync payroll button click
  const handleSyncToPayroll = async () => {
    if (!typedBusinessInfo?.id || !payrollPeriod) return;

    try {
      setIsLoadingTransactions(true);
      // Ensure sync modal is closed and response is cleared
      setSyncModalOpened(false);
      setPrepSyncResponse(null);

      // First, automatically sync payroll users before showing the modal
      const result = await syncPayrollUsers({ silent: true });

      if (!result.success) {
        notifyWarning(
          "Please ensure staff members are set up in RollFi before syncing payroll",
          { title: "Sync Required" },
        );
        return;
      }

      if (result.updatedCount > 0) {
        notifySuccess(
          `Synced ${result.updatedCount} staff member(s) with RollFi`,
          { title: "Users Synced" },
        );
      }

      // Check for incomplete transactions using helper function
      const incomplete = await checkForIncompleteTransactions(
        typedBusinessInfo.id,
        payrollPeriod,
        getTransactions,
      );
      if (incomplete.length > 0) {
        // Show incomplete transactions modal, don't fetch payroll data yet
        setIncompleteTransactions(incomplete);
        setIncompleteTransactionsModalOpened(true);
      } else {
        // No incomplete transactions, proceed with sync
        await fetchPayrollData();
        setSyncModalOpened(true);
      }
    } catch (error) {
      console.error("Error checking transactions:", error);
      notifyError("Failed to check transactions. Please try again.", {
        title: "Error",
      });
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  // Handle preparing for sync (after resolving or skipping incomplete transactions)
  const handlePrepForSync = async () => {
    setIncompleteTransactionsModalOpened(false);
    await fetchPayrollData();
    setSyncModalOpened(true);
  };

  const handlePayrollSync = async (data: SyncPayrollRequest) => {
    await syncRollfiPayroll(data);
  };

  const isLoading = salonLoading || isLoadingStatuses;

  return (
    <Stack gap="lg" style={{ position: "relative" }}>
      <LoadingOverlay visible={isLoading || isSyncingUsers} />

      {/* Payroll Portal Button - Prominent at top */}
      <Paper p="md" withBorder>
        <Group justify="space-between" align="center" wrap="wrap">
          <div>
            <Text size="lg" fw={600}>
              Payroll Portal
            </Text>
            <Text size="sm" c="dimmed">
              Access your RollFi payroll dashboard
            </Text>
          </div>
          <Button
            leftSection={<IconExternalLink size={18} />}
            onClick={openPayrollDashboard}
            color="var(--color-primary)"
            size={isMobile ? "sm" : "md"}
          >
            Open Payroll Portal
          </Button>
        </Group>
      </Paper>

      {/* Dashboard Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {/* Pay Period Card */}
        <Paper p="md" withBorder>
          <Group gap="sm" mb="sm">
            <IconCalendar size={20} color="var(--mantine-color-teal-6)" />
            <Text fw={600}>Current Pay Period</Text>
          </Group>
          {payrollPeriod ? (
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                {moment(payrollPeriod.payBeginDate).format("MMM D, YYYY")} -{" "}
                {moment(payrollPeriod.payEndDate).format("MMM D, YYYY")}
              </Text>
              <Group gap="xs">
                <Badge color="teal" variant="light" size="sm">
                  Pay Date: {moment(payrollPeriod.payDate).format("MMM D")}
                </Badge>
              </Group>
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">
              Loading pay period...
            </Text>
          )}
        </Paper>

        {/* Staff Status Card */}
        <Paper p="md" withBorder>
          <Group gap="sm" mb="sm">
            <IconUsers size={20} color="var(--mantine-color-teal-6)" />
            <Text fw={600}>Staff Status</Text>
            <Text size="xs" c="dimmed">
              {eligibleEmployees.length} employee(s) on payroll
            </Text>
          </Group>
          <Group gap="lg" wrap="wrap">
            <Group gap="xs">
              <IconUserCheck size={18} color="green" />
              <Text size="sm">
                <Text span fw={500}>
                  {clockedInCount}
                </Text>{" "}
                clocked in
              </Text>
            </Group>
            <Group gap="xs">
              <IconCoffee size={18} color="orange" />
              <Text size="sm">
                <Text span fw={500}>
                  {onBreakCount}
                </Text>{" "}
                on break
              </Text>
            </Group>
            <Group gap="xs">
              <IconUserX size={18} color="gray" />
              <Text size="sm">
                <Text span fw={500}>
                  {clockedOutCount}
                </Text>{" "}
                clocked out
              </Text>
            </Group>
          </Group>
        </Paper>

        {/* Sync Payroll Card */}
        <Paper p="md" withBorder>
          <Group gap="sm" mb="sm">
            <IconRefresh size={20} color="var(--mantine-color-teal-6)" />
            <Text fw={600}>Sync Payroll</Text>
          </Group>
          <Text size="sm" c="dimmed" mb="md">
            Sync employee commissions and tips to RollFi for the current pay
            period.
          </Text>
          <Button
            leftSection={<IconRefresh size={16} />}
            onClick={handleSyncToPayroll}
            variant="light"
            disabled={!payrollPeriod || isSyncingUsers}
            loading={isSyncingUsers || isLoadingTransactions}
            fullWidth
          >
            Sync to Payroll
          </Button>
        </Paper>
      </SimpleGrid>

      {/* Incomplete Transactions Modal */}
      <IncompleteTransactionsModal
        opened={incompleteTransactionsModalOpened}
        onClose={() => setIncompleteTransactionsModalOpened(false)}
        transactions={incompleteTransactions}
        onProceedAnyway={handlePrepForSync}
        onAllComplete={handlePrepForSync}
      />

      {/* Payroll Sync Modal */}
      {typedBusinessInfo?.rollfi__company_id && payrollPeriod && (
        <PayrollSyncConfirmationModal
          opened={syncModalOpened}
          onClose={() => setSyncModalOpened(false)}
          onSuccess={() => {
            // Optionally refresh data after sync
          }}
          eligibleStaff={prepSyncResponse?.eligibleStaff || []}
          syncPayload={prepSyncResponse?.syncPayload || null}
          onSync={handlePayrollSync}
          hasUnapprovedEntries={timesheetData.timeCards.some(
            (tc) =>
              tc.approvalStatus !== "Approved" &&
              tc.approvalStatus !== "Synced",
          )}
        />
      )}
    </Stack>
  );
}
